import { validationTypes } from 'components/FormCreator/types';
import _ from 'lodash';

export enum NewAccountUserPermissions {
	SIGNER = 'Signer',
	OPERATIONER = 'Operationer',
	VIEWER = 'Viewer',
}

export const NewAccountCreationSource = {
	HyperWebhook: 'hyperWebhook',
	OpenManual: 'openManual',
} as const;

export type NewAccountCreationSource =
	(typeof NewAccountCreationSource)[keyof typeof NewAccountCreationSource];

export interface INewAccount {
	id?: number;
	accountNameEnglish?: string;
	accountNameHebrew?: string;
	hyperAccountNumber?: string;
	creationSource?: NewAccountCreationSource;
	entityType?: string;
	identityNumber?: string;
	addressEnglish?: string;
	addressHebrew?: string;
	cityEnglish?: string;
	cityHebrew?: string;
	countryId?: number;
	zipCode?: number;
	maxForPayment?: number;
	maxForConvert?: number;
	dealerId?: number | null;
	customerSuccessManagerId?: number | null;
	agentId?: number | null;
	leadId?: number | null;
	riskLevel?: string;
	dateOfIncorporation?: string;
	industryId?: number;
	businessWebsiteUrl?: string;
	expectedMonthlyActivityValueIls?: number;
	expectedMonthlyActivityVolume?: number;
	expectedPaymentCurrencyId?: number;
	expectedPaymentCountryIds?: number[];
	newUsers: INewUser[];
	newBankAccounts: INewBankAccount[];
	allDenyMessages?: any;
	allowedPaymentCategoryIds?: number[];
}

export const NewAccountInitialValues = {};

export interface INewBankAccount {
	id?: number;
	accountOwnerName?: string;
	bankBranch?: string;
	accountNumber?: string;
	iban?: string;
	newAccountId?: number;
}

export interface INewUser {
	id?: number;
	firstNameHebrew?: string;
	firstNameEnglish?: string;
	lastNameHebrew?: string;
	lastNameEnglish?: string;
	occupation?: string;
	identificationNumber?: string;
	dateOfBirth?: Date;
	phoneNumber?: string;
	emailAddress?: string;
	maxForPayment?: number;
	maxForConvert?: number;
	address?: string;
	city?: string;
	countryId?: number;
	birthCountryId?: number;
	role?: string;
	permission?: NewAccountUserPermissions;
	newAccountId?: number;
}

type birthDate = 'day' | 'month' | 'year';
type extraProps = 'legalStatus' | 'identificationType' | 'gender';
type newUserProp = keyof INewUser | birthDate | extraProps;

export const newUserProps: Array<newUserProp> = [
	'id',
	'firstNameHebrew',
	'firstNameEnglish',
	'lastNameHebrew',
	'lastNameEnglish',
	'occupation',
	'identificationNumber',
	'dateOfBirth',
	'phoneNumber',
	'emailAddress',
	'maxForPayment',
	'maxForConvert',
	'address',
	'city',
	'countryId',
	'birthCountryId',
	'role',
	'permission',
	'newAccountId',
	'legalStatus',
	'identificationType',
	'gender',
	// 'day',
	// 'month',
	// 'year',
];

export const newAccountFieldsValidations: {
	[key in keyof Partial<INewAccount>]: validationTypes[];
} = {
	accountNameEnglish: [validationTypes.requierd],
	accountNameHebrew: [validationTypes.requierd],
	hyperAccountNumber: [validationTypes.requierd],
	entityType: [validationTypes.requierd],
	identityNumber: [validationTypes.requierd],
	addressEnglish: [validationTypes.requierd],
	addressHebrew: [validationTypes.requierd],
	cityEnglish: [validationTypes.requierd],
	cityHebrew: [validationTypes.requierd],
	countryId: [validationTypes.requierd],
	maxForPayment: [validationTypes.requierd],
	maxForConvert: [validationTypes.requierd],
	riskLevel: [validationTypes.requierd],
};

export enum NewAccountFieldsType {
	REPORT_TYPE = 'ReportType',
	DATE_RANGE = 'DateRange',
	COMPANY_NAME = 'CompanyName',
}

export enum companyType {
	INDIVIDUAL = 'INDIVIDUAL',
	CORPORATE = 'CORPORATE',
}

export enum newAccountsSteps {
	CUSTOMER_DETAILS = 'CUSTOMER_DETAILS',
	USER_DETAILS = 'USER_DETAILS',
	BANK_ACCOUNT_DETAILS = 'BANK_ACCOUNT_DETAILS',
	CONCLUSION_AND_APPROVE = 'CONCLUSION_AND_APPROVE',
}

export enum AccountEntityType {
	COMPANY = 'COMPANY',
	CORPORATION = 'CORPORATION',
}

export enum NewAccountStepStatus {
	NONE = 'NONE',
	APPROVED_ACCOUNT_DETAILS = 'APPROVED_ACCOUNT_DETAILS',
	APPROVED_USERS = 'APPROVED_USERS',
	APPROVED_BANK_ACCOUNTS = 'APPROVED_BANK_ACCOUNTS',
	APPROVED = 'APPROVED',
	DENIED = 'DENIED',
}

export enum NewAccountStatus {
	DRAFT = 'DRAFT',
	IN_REVIEW = 'IN_REVIEW',
	APPROVED = 'APPROVED',
	DENIED = 'DENIED',
}

export enum NewUserStatus {
	DRAFT = 'DRAFT',
	IN_REVIEW = 'IN_REVIEW',
	APPROVED = 'APPROVED',
}

export enum NewBankAccountStatus {
	DRAFT = 'DRAFT',
	IN_REVIEW = 'IN_REVIEW',
	APPROVED = 'APPROVED',
}

export const newAccountPayload = [
	'accountNameEnglish',
	'accountNameHebrew',
	'hyperAccountNumber',
	'entityType',
	'addressEnglish',
	'addressHebrew',
	'cityEnglish',
	'cityHebrew',
	'zipCode',
	'maxForPayment',
	'maxForConvert',
	'dealerId',
	'customerSuccessManagerId',
	'agentId',
	'leadId',
	'riskLevel',
	'countryId',
	'identityNumber',
	'allowedPaymentCategoryIds',
	'dateOfIncorporation',
	'industryId',
	'businessWebsiteUrl',
	'expectedAnnualPaymentsVolume',
	'expectedMonthlyPaymentsCount',
	'expectedPaymentCountryIds',
	'expectedPaymentCurrencyId',
];

export enum continueOptions {
	SAVE = 'save',
	SAVE_AND_NEW = 'save_and_new',
	SAVE_AND_CONTINUE = 'save_and_continue',
}

export const accountMaxForPaymentAndConvert = 5000000;

export const dummyCityNames = [
	'-',
	'x',
	'n/a',
	'test',
	'na',
	'xx',
	'xxx',
	'tbd',
	'unknown',
	'none',
	'city',
	'address',
	'0',
	'.',
	'--',
	'null',
	'no',
];

export const noBusinessWebsiteValue = 'no_website_available';

export const businessWebsitePrefixes = ['https://', 'http://'];

export const businessWebsiteUrlMaxLength = 400;

export const maxExpectedPaymentCountries = 3;

export const expectedAnnualPaymentsVolumeByStoredValue: Record<number, string> = {
	20833: '0-500K',
	104167: '500K-2M',
	291667: '2M-5M',
	625000: '5M-10M',
	833333: '10M+',
};

export const expectedMonthlyPaymentsCountByStoredValue: Record<number, string> = {
	1: '0-2',
	4: '3-5',
	8: '6-10',
	10: '10+',
};

export const expectedAnnualPaymentsVolumeOptions = _.map(expectedAnnualPaymentsVolumeByStoredValue, (token) => ({
	value: token,
	label: token,
}));

export const expectedMonthlyPaymentsCountOptions = _.map(expectedMonthlyPaymentsCountByStoredValue, (token) => ({
	value: token,
	label: token,
}));

export const newAccountInvalidFieldLabels: Record<string, string> = {
	dateOfIncorporation: 'page.accounts.new_account.customer.date_of_incorporation',
	industryId: 'page.accounts.new_account.customer.industry',
	businessWebsiteUrl: 'page.accounts.new_account.customer.business_website',
	expectedMonthlyActivityValueIls: 'page.accounts.new_account.customer.expected_annual_payments_volume',
	expectedMonthlyActivityVolume: 'page.accounts.new_account.customer.expected_monthly_payments_count',
	expectedPaymentCurrencyId: 'page.accounts.new_account.customer.expected_payment_currency',
	expectedPaymentCountryIds: 'page.accounts.new_account.customer.expected_payment_countries',
	zipCode: 'label.postalCode',
	riskLevel: 'page.accounts.new_account.customer.client_risk_level',
	cityEnglish: 'page.accounts.user_details.city',
	addressEnglish: 'page.accounts.customer_details.street_number_english',
	countryId: 'label.country',
};
